// Client-side image downscaling before upload. Photos go through the API
// (a Vercel request body tops out around 4.5MB, and base64 adds a third), so
// a phone photo is resized to a sensible long edge and re-encoded first.

const MAX_EDGE = 1600;
const QUALITY_STEPS = [0.82, 0.7, 0.6];
export const MAX_RAW_BYTES = 3 * 1024 * 1024;

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/** Falls back to the original file when the browser can't decode it (HEIC outside Safari). */
export async function prepareImage(file: File): Promise<{ blob: Blob; contentType: string }> {
  const original = { blob: file as Blob, contentType: file.type || 'image/jpeg' };
  if (typeof createImageBitmap !== 'function') return original;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return original;
  }
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= 600 * 1024) return original;

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return original;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    // Screenshots keep PNG transparency; photos become JPEG.
    const keepPng = file.type === 'image/png';
    const type = keepPng ? 'image/png' : 'image/jpeg';
    for (const quality of QUALITY_STEPS) {
      const blob = await canvasToBlob(canvas, type, quality);
      if (blob && (blob.size <= MAX_RAW_BYTES || keepPng)) return { blob, contentType: type };
    }
    const last = await canvasToBlob(canvas, 'image/jpeg', QUALITY_STEPS[QUALITY_STEPS.length - 1]);
    return last ? { blob: last, contentType: 'image/jpeg' } : original;
  } finally {
    bitmap.close?.();
  }
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.readAsDataURL(blob);
  });
}
